/**
 * Enterprise Lessons Page
 * 5 lesson cards + FREE PLAY + BACK TO SESSIONS
 *
 * Merges localStorage progress (step-level detail) with server-side
 * progress (database completion state visible to login-v2sims dashboard).
 */

import { navigate } from '../router';
import { getAllProgress, clearAllProgress, clearLessonProgress } from '../stores/enterpriseStore';

interface LessonEntry {
  id: string;
  name: string;
  comingSoon?: boolean;
}

const LESSONS: LessonEntry[] = [
  { id: 'ent-gfx-01', name: 'NOVP Basics' },
  { id: 'ent-gfx-02', name: 'Live Audio/Video & Scoreboard Templates' },
  { id: 'ent-gfx-03', name: 'Building Shots & Media Sequence' },
  { id: 'ent-gfx-04', name: 'Video Switcher & Data Controllers' },
  { id: 'ent-gfx-05', name: 'Produce a Show' },
];

/** Fetch server-side completion state (from enterprise_progress DB table). */
async function fetchServerProgress(): Promise<Record<string, boolean>> {
  try {
    const res = await fetch('/api/v1/enterprise/progress', { credentials: 'include' });
    if (!res.ok) return {};
    const json = await res.json();
    const map: Record<string, boolean> = {};
    for (const entry of json.data?.progress ?? []) {
      if (entry.isComplete) {
        map[entry.lessonId] = true;
      }
    }
    return map;
  } catch {
    return {};
  }
}

export async function renderLessons(container: HTMLElement): Promise<void> {
  const localProgress = getAllProgress();
  const serverComplete = await fetchServerProgress();

  const cards = LESSONS.map((lesson, i) => {
    const p = localProgress[lesson.id];
    // A lesson is complete if either localStorage or server says so
    const isComplete = p?.isComplete || serverComplete[lesson.id];
    let statusText = 'Not Started';
    let statusClass = '';
    let cardClass = 'lesson-card';

    if (lesson.comingSoon) {
      statusText = 'Coming Soon';
      cardClass += ' coming-soon';
    } else if (isComplete) {
      statusText = '\u2713 Complete';
      statusClass = 'completed';
      cardClass += ' clickable';
    } else if (p && p.completedStepIds.length > 0) {
      statusText = `In Progress (${p.completedStepIds.length} steps)`;
      statusClass = 'in-progress';
      cardClass += ' clickable';
    } else {
      cardClass += ' clickable';
    }

    return `
      <div class="${cardClass}" data-lesson-id="${lesson.id}" ${lesson.comingSoon ? '' : 'role="button" tabindex="0"'}>
        <span class="lesson-name">${i + 1}. ${lesson.name}</span>
        <span class="lesson-status ${statusClass}">${statusText}</span>
      </div>
    `;
  }).join('');

  container.innerHTML = `
    <div class="enterprise-page">
      <h2>TRAINING LESSONS</h2>
      <div class="lesson-grid">
        ${cards}
      </div>
      <button class="ent-btn" id="ent-free-play" style="background:#2a9f45;">FREE PLAY</button>
      <div style="display:flex;gap:12px;align-items:center;">
        <button class="ent-btn" id="ent-back-sessions">BACK TO SESSIONS</button>
        <button class="ent-btn" id="ent-reset-progress" style="background:#555;color:#ccc;">RESET PROGRESS</button>
      </div>
    </div>
  `;

  // Click handlers for lesson cards
  container.querySelectorAll<HTMLElement>('.lesson-card.clickable').forEach((card) => {
    card.addEventListener('click', () => {
      const lessonId = card.dataset.lessonId;
      if (lessonId) {
        navigate(`/enterprise-lesson/${lessonId}`);
      }
    });
  });

  container.querySelector('#ent-free-play')!
    .addEventListener('click', () => navigate('/sim'));

  container.querySelector('#ent-back-sessions')!
    .addEventListener('click', () => navigate('/sessions'));

  container.querySelector('#ent-reset-progress')!
    .addEventListener('click', () => {
      openResetProgressModal(container);
    });
}

function openResetProgressModal(container: HTMLElement): void {
  const localProgress = getAllProgress();

  // Show every lesson; ones with progress are actionable, others are dimmed
  const rows = LESSONS
    .filter((l) => !l.comingSoon)
    .map((lesson) => {
      const p = localProgress[lesson.id];
      const hasProgress = !!p && (p.isComplete || (p.completedStepIds?.length ?? 0) > 0);
      const status = !hasProgress
        ? 'No progress'
        : p.isComplete
          ? '✓ Complete'
          : `In Progress (${p.completedStepIds.length} steps)`;
      return `
        <div class="reset-row">
          <div class="reset-row-info">
            <div class="reset-row-name">${lesson.name}</div>
            <div class="reset-row-status">${status}</div>
          </div>
          <button
            class="ent-btn reset-row-btn"
            data-lesson-id="${lesson.id}"
            ${hasProgress ? '' : 'disabled'}
            style="${hasProgress ? '' : 'opacity:0.4;cursor:default;'}"
          >Reset</button>
        </div>
      `;
    }).join('');

  const overlay = document.createElement('div');
  overlay.className = 'reset-progress-overlay';
  overlay.innerHTML = `
    <div class="reset-progress-modal">
      <h3>Reset Progress</h3>
      <p class="reset-progress-hint">Choose a lesson to reset, or reset everything.</p>
      <div class="reset-progress-list">${rows}</div>
      <div class="reset-progress-actions">
        <button class="ent-btn" id="reset-cancel" style="background:#444;">Cancel</button>
        <button class="ent-btn" id="reset-all" style="background:#a8423a;">Reset All</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const close = () => overlay.remove();
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  overlay.querySelector('#reset-cancel')!.addEventListener('click', close);

  overlay.querySelector('#reset-all')!.addEventListener('click', () => {
    clearAllProgress();
    close();
    renderLessons(container);
  });

  overlay.querySelectorAll<HTMLButtonElement>('.reset-row-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const lessonId = btn.dataset.lessonId;
      if (!lessonId) return;
      clearLessonProgress(lessonId);
      close();
      renderLessons(container);
    });
  });
}
